import clsx from "clsx";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Coins, AlertCircle } from "lucide-react";
import { useCreditBalance } from "../../hooks/useCreditBalance";
import { useAuth } from "../../contexts/AuthContext";
import { Action, projectSubject, useAppAbility } from "common/access";
import { Button } from "../common/Button";
import { getProgressFillColor, getUsedPercentage } from "./utils";

const navigateToNewProject = () => {
  window.location.href = "/project/new";
};

export const CreateProjectCreditsPanel = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const ability = useAppAbility();
  const { creditBalance, isLoading, isQuantitySurveyor } = useCreditBalance();

  const canCreate = useMemo(
    () =>
      ability.can(
        Action.Create,
        projectSubject({ organizationId: user?.organizationId }),
      ),
    [ability, user?.organizationId],
  );

  const createProjectIcon = <Plus size={18} />;

  if (!canCreate) {
    return null;
  }

  if (!isQuantitySurveyor) {
    return (
      <div className="w-full flex-shrink-0 sm:w-auto">
        <Button
          variant="primary"
          size="md"
          startAdornment={createProjectIcon}
          onClick={navigateToNewProject}
          className="w-full sm:w-auto"
        >
          {t("dashboard.create_new_project")}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-28 w-full flex-shrink-0 animate-pulse rounded-2xl bg-gray-200 sm:w-80" />
    );
  }

  if (!creditBalance) {
    return (
      <div className="w-full flex-shrink-0 sm:w-auto">
        <Button
          variant="primary"
          size="md"
          startAdornment={createProjectIcon}
          onClick={navigateToNewProject}
          className="w-full sm:w-auto"
        >
          {t("dashboard.create_new_project")}
        </Button>
      </div>
    );
  }

  if (creditBalance.available <= 0) {
    return (
      <div className="flex min-h-28 w-full flex-shrink-0 flex-col justify-center gap-2 sm:w-64">
        <div className="flex items-center gap-1.5 px-1 text-sm font-semibold text-amber-600">
          <AlertCircle size={14} className="shrink-0" />
          <span>{t("profile.credits.noCreditsShort")}</span>
        </div>
        <Button
          variant="primary"
          size="md"
          startAdornment={createProjectIcon}
          onClick={navigateToNewProject}
          className="w-full"
        >
          {t("dashboard.create_new_project")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-28 w-full flex-shrink-0 flex-col justify-center gap-3 rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-500 p-4 text-white shadow-md sm:w-80">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="group relative flex cursor-help items-center gap-1.5">
            <Coins size={16} className="shrink-0 text-white/80" />
            <span className="text-sm font-bold leading-none">
              {creditBalance.available}
            </span>
            <span className="whitespace-nowrap text-xs text-white/80">
              {t("profile.credits.available")}
            </span>
            <div className="pointer-events-none absolute left-0 top-full z-20 mt-2 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
              {t("profile.credits.tooltip")}
              <span className="absolute bottom-full left-4 h-2 w-2 translate-y-1/2 rotate-45 bg-slate-900" />
            </div>
          </div>
          <span className="whitespace-nowrap text-xs text-white/60">
            {creditBalance.usedCredits} / {creditBalance.totalCredits}{" "}
            {t("profile.credits.used")}
          </span>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-white/20">
          <div
            className={clsx(
              "h-full rounded-full transition-all duration-500",
              getProgressFillColor(
                creditBalance.available,
                creditBalance.totalCredits,
              ),
            )}
            style={{
              width: `${getUsedPercentage(creditBalance.usedCredits, creditBalance.totalCredits)}%`,
            }}
          />
        </div>
      </div>
      <Button
        variant="inverted"
        size="md"
        startAdornment={createProjectIcon}
        onClick={navigateToNewProject}
        className="w-full shadow-sm"
      >
        {t("dashboard.create_new_project")}
      </Button>
    </div>
  );
};
